// passwordReset.ts — self-service password reset against the DRF identity
// endpoints. The request endpoint is enumeration-safe (always 200), so callers
// must show the same neutral message regardless of outcome. The confirm
// endpoint returns 400 with `{ detail }` for an invalid/expired token.

/** Ask the backend to email a reset link. Always resolves (the endpoint is
 * enumeration-safe and returns 200 whether or not the account exists). */
export async function requestPasswordReset(email: string): Promise<void> {
  const base = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/?$/, '');
  await fetch(`${base}/api/identity/password-reset/request/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email }),
  });
}

/** Set a new password using the token from the reset email. Throws an Error
 * carrying the backend `detail` (e.g. invalid/expired token) on non-2xx. */
export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  const base = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/?$/, '');
  const res = await fetch(`${base}/api/identity/password-reset/confirm/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!res.ok) {
    let detail = `Password reset failed (${res.status})`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body?.detail) detail = body.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
}
